import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CalendarCheck, ShieldCheck, WarningCircle, Plus, CheckCircle, Circle } from "@phosphor-icons/react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function VisitDialog({ project, open, onClose, onSaved }) {
  const [v, setV] = useState({ type: "Revizie", date: "", notes: "" });
  const save = async () => {
    try { await api.post(`/projects/${project.id}/maintenance`, v); toast.success("Vizită programată"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Vizită mentenanță — {project?.code}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Tip</label>
            <Select value={v.type} onValueChange={(x) => setV({ ...v, type: x })}>
              <SelectTrigger data-testid="sb-visit-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Revizie">Revizie periodică</SelectItem><SelectItem value="Intervenție garanție">Intervenție garanție</SelectItem><SelectItem value="Service">Service</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Data</label><Input type="date" value={v.date} onChange={(e) => setV({ ...v, date: e.target.value })} data-testid="sb-visit-date" /></div>
          <div className="grid gap-1.5"><label className="text-xs font-bold uppercase text-muted-foreground">Note</label><Textarea value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="sb-visit-save">Programează</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ServiceBook() {
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = () => api.get("/service-book").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runReminders = async () => {
    try { const { data } = await api.post("/service-book/reminders/run"); toast.success(`Remindere trimise — vizite: ${data.visits}, garanții: ${data.warranty}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const startWarranty = async (p) => {
    try { await api.post(`/projects/${p.id}/warranty/start`, {}); toast.success("Garanție pornită"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggle = async (p, vid) => {
    try { await api.post(`/projects/${p.id}/maintenance/${vid}/toggle`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 fade-up" data-testid="servicebook-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Montaj · Post-livrare</p>
          <h1 className="font-head text-4xl font-extrabold tracking-tighter">Carnet Service & Mentenanță</h1>
          <p className="mt-1 text-sm text-muted-foreground">Proiecte livrate/finalizate: stare garanție și vizite de mentenanță la beneficiar.</p>
        </div>
        <Button variant="outline" onClick={runReminders} data-testid="sb-reminders-btn"><CalendarCheck size={16} className="mr-1.5" /> Verifică remindere</Button>
      </div>

      <div className="space-y-3">
        {rows.map((p) => (
          <div key={p.id} className="rounded-xl border border-border bg-card p-4" data-testid={`sb-${p.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-head text-lg font-bold">{p.code}</span>
                  <span className="text-sm text-muted-foreground">{p.name}</span>
                </div>
                <p className="text-sm text-muted-foreground">{p.beneficiary || p.client}</p>
              </div>
              <div className="text-right">
                {p.warranty_start ? (
                  <span className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-bold ${p.warranty_active ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-400"}`}>
                    {p.warranty_active ? <ShieldCheck size={13} /> : <WarningCircle size={13} />}
                    Garanție {p.warranty_active ? "activă" : "expirată"} · până la {fmt(p.warranty_expiry)}
                  </span>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => startWarranty(p)} data-testid={`sb-startw-${p.id}`}>Pornește garanția</Button>
                )}
              </div>
            </div>
            <div className="mt-3 border-t border-border pt-3">
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-sm font-bold">Vizite de mentenanță</h4>
                <Button size="sm" onClick={() => setDlg(p)} data-testid={`sb-add-${p.id}`}><Plus size={14} className="mr-1" /> Programează vizită</Button>
              </div>
              <div className="space-y-1.5">
                {(p.maintenance_visits || []).map((v) => (
                  <button key={v.id} onClick={() => toggle(p, v.id)} className="flex w-full items-center gap-2 rounded-lg border border-border/60 px-3 py-2 text-left text-sm hover:bg-white/5" data-testid={`sb-visit-${v.id}`}>
                    {v.done ? <CheckCircle size={16} weight="fill" className="text-emerald-400" /> : <Circle size={16} className="text-muted-foreground" />}
                    <span className={v.done ? "text-muted-foreground line-through" : ""}>{v.type} · {fmt(v.date)}</span>
                    {v.notes && <span className="text-muted-foreground">— {v.notes}</span>}
                  </button>
                ))}
                {(p.maintenance_visits || []).length === 0 && <p className="text-sm text-muted-foreground">Nicio vizită programată.</p>}
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground"><CalendarCheck size={22} className="mx-auto mb-2 text-muted-foreground" /> Niciun proiect livrat/finalizat încă.</p>}
      </div>

      {dlg && <VisitDialog project={dlg} open={!!dlg} onClose={() => setDlg(null)} onSaved={load} />}
    </div>
  );
}
